import axios from 'axios';
import { getCccApiBase } from '../config';

const readAuthToken = () =>
  localStorage.getItem('jwt_token') || localStorage.getItem('access_token');

const authHeaders = () => {
  const token = readAuthToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const authHeadersMultipart = () => {
  const token = readAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export type WorkspaceMotivoData = {
  motivo_keys?: string[] | null;
  motivo_key?: string | null;
  feedback?: string | null;
  updated_at?: string | null;
  updated_by?: number | null;
  updated_by_name?: string | null;
};

/** @deprecated Use WorkspaceMotivoData */
export type SemInteresseMotivoData = WorkspaceMotivoData;

export type FidelizadosServicoItem = {
  nome?: string | null;
  quantidade?: number | null;
  valor_fatura?: number | null;
};

export type FidelizadosDadosData = {
  fornecedor_atual?: string | null;
  data_inicio_contrato?: string | null;
  servicos?: FidelizadosServicoItem[];
  updated_at?: string | null;
  updated_by?: number | null;
  updated_by_name?: string | null;
};

export type EnvioPropostaData = {
  proposta_enviada?: boolean | null;
  data_envio?: string | null;
  enviado_para?: string | null;
  motivo_nao_enviada?: string | null;
  motivo_nao_enviada_outro?: string | null;
  fornecedor_atual?: string | null;
  data_fim_contrato?: string | null;
  updated_at?: string | null;
  updated_by?: number | null;
  updated_by_name?: string | null;
};

export type AssinadoDadosData = {
  assinado?: boolean | null;
  data_assinatura?: string | null;
  motivo_nao_assinado?: string | null;
  motivo_nao_assinado_outro?: string | null;
  fornecedor_atual?: string | null;
  data_fim_contrato?: string | null;
  updated_at?: string | null;
  updated_by?: number | null;
  updated_by_name?: string | null;
};

export type TentativaContactoItem = {
  id?: string;
  date?: string;
  time?: string;
  created_at?: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
};

export type TentativasContactoData = {
  items?: TentativaContactoItem[];
  updated_at?: string | null;
  updated_by?: number | null;
  updated_by_name?: string | null;
};

export type CccHistoryEvent = {
  event_type:
    | 'marcacao_criada'
    | 'lead_created'
    | 'workspace_claim'
    | 'status_update'
    | 'solicitar_proposta'
    | 'alteracao_comercial';
  title: string;
  timestamp: string;
  by_id?: number | null;
  by_name?: string | null;
  details?: {
    old_substage?: string | null;
    new_substage?: string | null;
    source?: string | null;
    marcacao_id?: string;
    lead_id?: string;
    empresa?: string;
    responsavel?: string;
    nome_empresa?: string | null;
    responsavel_empresa?: string | null;
    contacto?: string | null;
    email?: string | null;
    proposta_id?: string | null;
    old_user_id?: number | null;
    old_user_name?: string | null;
    new_user_id?: number | null;
    new_user_name?: string | null;
    workspace?: string | null;
  };
};

export type CccClientWorked = {
  id: string;
  workspace: 'prospeccao' | 'recuperacao' | 'retencao';
  workspace_label: string;
  entity_id: string;
  entity_label: string;
  responsavel: string | null;
  contacto: string | null;
  produto_label: string | null;
  status_key: string | null;
  status_label: string | null;
  worked_by: Array<{ user_id: number; user_name: string }>;
  work_reasons: string[];
  work_reason_labels: string[];
  last_activity_at: string | null;
};

export type CccLeadNote = {
  text: string;
  at: string;
  by_name?: string;
  by_id?: number;
};

export type CccProposalStatRow = {
  id: string;
  workspace: 'prospeccao' | 'recuperacao' | 'retencao';
  workspace_label: string;
  entity_id: string;
  proposta_id: string;
  nome_empresa: string;
  servico: string;
  servico_kind: string;
  data_pedido: string | null;
  status_key: string;
  status: string;
  submitted_by_id: number | null;
  submitted_by_name: string;
  backoffice_claimed_by: string;
  is_roadtrip?: boolean;
};

export type CccContractSubmissionRow = {
  row_id: string;
  workspace: 'prospeccao' | 'recuperacao' | 'retencao';
  workspace_label: string;
  submitted_at: string | null;
  urgente: boolean;
  company_name: string;
  nif: string;
  submitted_by_id: number | null;
  submitted_by_name: string;
  codigo_comercial?: string;
  status: string;
  status_label: string;
  backoffice_claimed_by: string;
  id_proposta?: string;
  cpe?: string;
  cpe_display?: string;
  cpe_count?: number;
  cpe_list?: string[];
  cui?: string;
  cui_display?: string;
  cui_count?: number;
  cui_list?: string[];
  service_type_label?: string;
  representative_name?: string;
  representative_contact?: string;
  data_ativacao?: string | null;
};

export type CccSignatureStatRow = {
  id: number;
  workspace: string;
  workspace_label: string;
  company_name: string;
  nif: string;
  iban: string;
  bic: string;
  swift: string | null;
  representative_name: string;
  representative_email: string;
  representative_contact: string;
  submitted_by_id: number | null;
  submitted_by_name: string;
  status: string;
  status_raw: string;
  created_at: string | null;
  completed_cancelled_at: string | null;
  expected_signature_at: string | null;
  associated_proposal_id: string | null;
};

export type RecuperacaoHistoryEvent = CccHistoryEvent;

export interface RecuperacaoMarcacao {
  id: number;
  marcacao_id: string;
  date?: string;
  hora_inicio?: string;
  hora_fim?: string;
  marcacao_description?: string | null;
  produto: string;
  product_details?: Record<string, unknown>;
  nome_empresa: string;
  nome_responsavel: string;
  morada?: string;
  contacto_cliente?: string;
  codigo_postal1?: string | null;
  codigo_postal2?: string | null;
  link_google_maps?: string | null;
  /** When claimed: mirrors CCC workspace substage for tables/filters. Use ccc_substage as source of truth. */
  gestao_substage?: string;
  /** CCC workspace substage (ccc_recuperacao_workspaces); only changes via CCC Recuperação actions. */
  ccc_substage?: string;
  /** Comercial gestão substage on DB row (snapshot; never overwritten by CCC). */
  comercial_gestao_substage?: string;
  form_data?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  tenant?: string;
  days_in_recuperacao?: number;
  is_available?: boolean;
  is_mine?: boolean;
  /** Pool: Pedido de proposta made by Comercial (not CCC). */
  has_comercial_pedido?: boolean;
  pool_tags?: string[];
  eligibility_became_at?: string | null;
  recuperacao_claim?: {
    claimed_by?: number | null;
    claimed_by_name?: string | null;
    claimed_at?: string | null;
    estado_anterior_proposta?: string | null;
    estado_anterior_cliente?: string | null;
    ccc_substage?: string;
    comercial_snapshot?: {
      gestao_substage?: string;
      gestao_substage_label?: string;
      estado_proposta?: string;
      estado_proposta_label?: string;
      estado_anterior_display?: string;
      captured_at?: string;
    };
    notes?: Array<{
      text: string;
      at: string;
      by_name?: string;
      by_id?: number;
    }>;
    sem_interesse_motivo?: WorkspaceMotivoData | null;
    pendente_motivo?: WorkspaceMotivoData | null;
    fidelizados_dados?: FidelizadosDadosData | null;
    envio_proposta?: EnvioPropostaData | null;
    assinado_dados?: AssinadoDadosData | null;
    tentativas_contacto?: TentativasContactoData | null;
  };
  assignees?: Array<{
    id: number;
    name: string;
    is_deleted: boolean;
    is_suspended: boolean;
    is_hidden: boolean;
    status_label?: string | null;
  }>;
  has_deleted_or_suspended_assignee?: boolean;
}

export type CccWorkspaceKind = 'prospeccao' | 'recuperacao' | 'retencao';

export type RetencaoHistoryEvent = CccHistoryEvent;

export interface RetencaoMarcacao {
  id: number;
  marcacao_id: string;
  date?: string;
  hora_inicio?: string;
  hora_fim?: string;
  marcacao_description?: string | null;
  produto: string;
  product_details?: Record<string, unknown>;
  nome_empresa: string;
  nome_responsavel: string;
  morada?: string;
  contacto_cliente?: string;
  codigo_postal1?: string | null;
  codigo_postal2?: string | null;
  link_google_maps?: string | null;
  gestao_substage?: string;
  ccc_substage?: string;
  comercial_gestao_substage?: string;
  form_data?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  tenant?: string;
  days_in_retencao?: number;
  is_available?: boolean;
  is_mine?: boolean;
  retencao_claim?: RecuperacaoMarcacao['recuperacao_claim'];
  assignees?: RecuperacaoMarcacao['assignees'];
  has_deleted_or_suspended_assignee?: boolean;
}

export interface RetencaoClient {
  id: string;
  nome_empresa: string;
  nif: string;
  data_cancelamento?: string | null;
  motivo_cancelamento?: string | null;
  multa?: string | null;
  objetivo?: string | null;
  tempo_resposta?: string | null;
  responsavel_empresa?: string;
  contacto?: string;
  email?: string;
  google_maps_link?: string | null;
  ccc_substage: string;
  source?: string;
  created_by?: number;
  assigned_to?: number | null;
  is_in_pool?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export type CccWorkspaceSearchMatch = {
  workspace: CccWorkspaceKind;
  id: string;
  substage: string;
  owner_user_id: number | null;
  owner_user_name?: string | null;
  label: string;
  subtitle?: string | null;
  is_available?: boolean;
};

export interface ProspeccaoLead {
  id: string;
  nome_empresa: string;
  responsavel_empresa: string;
  contacto: string;
  email: string;
  google_maps_link?: string | null;
  produto?: string[] | null;
  ccc_substage: string;
  tentativas_contacto?: TentativasContactoData | null;
  sem_interesse_motivo?: WorkspaceMotivoData | null;
  pendente_motivo?: WorkspaceMotivoData | null;
  fidelizados_dados?: FidelizadosDadosData | null;
  envio_proposta?: EnvioPropostaData | null;
  assinado_dados?: AssinadoDadosData | null;
  proposal_requests?: Record<string, unknown>[] | null;
  marcacao_form_data?: Record<string, unknown> | null;
  marcacao_event_id?: number | null;
  marcacao_id?: string | null;
  created_by?: number | null;
  assigned_to?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  notes?: Array<{
    id?: string;
    text: string;
    at?: string;
    by_id?: number;
    by_name?: string;
    updated_at?: string;
    updated_by_name?: string;
  }>;
}

export const cccApi = {
  async getProspeccaoLeads(params?: { search?: string }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/prospeccao/leads`, {
      headers: authHeaders(),
      params,
    });
    return data as { success: boolean; leads: ProspeccaoLead[]; message?: string };
  },

  async createProspeccaoLead(payload: {
    nome_empresa: string;
    responsavel_empresa: string;
    contacto: string;
    email?: string | null;
    google_maps_link?: string | null;
    produto?: string[] | null;
    ccc_substage?: string;
  }) {
    const { data } = await axios.post(`${getCccApiBase()}/api/ccc/prospeccao/leads`, payload, {
      headers: authHeaders(),
    });
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoLead(
    leadId: string,
    payload: Partial<{
      nome_empresa: string;
      responsavel_empresa: string;
      contacto: string;
      email?: string | null;
      google_maps_link?: string | null;
      produto?: string[] | null;
      ccc_substage: string;
    }>
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoLeadSubstage(leadId: string, ccc_substage: string) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/substage`,
      { ccc_substage },
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async addProspeccaoLeadNote(leadId: string, text: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/notes`,
      { text },
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoLeadNote(leadId: string, noteId: string, text: string) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/notes/${encodeURIComponent(noteId)}`,
      { text },
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async deleteProspeccaoLeadNote(leadId: string, noteId: string) {
    const { data } = await axios.delete(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/notes/${encodeURIComponent(noteId)}`,
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoTentativasContacto(
    leadId: string,
    payload: {
      items: Array<{ id?: string; date: string; time: string }>;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/tentativas-contacto`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoSemInteresseMotivo(
    leadId: string,
    payload: { motivo_keys?: string[]; motivo_key?: string | null; feedback?: string }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/sem-interesse-motivo`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoPendenteMotivo(
    leadId: string,
    payload: { motivo_keys?: string[]; motivo_key?: string | null; feedback?: string }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/pendente-motivo`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoFidelizadosDados(
    leadId: string,
    payload: {
      fornecedor_atual?: string | null;
      data_inicio_contrato?: string | null;
      servicos?: FidelizadosServicoItem[];
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/fidelizados-dados`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoEnvioProposta(
    leadId: string,
    payload: {
      proposta_enviada?: boolean | null;
      data_envio?: string | null;
      enviado_para?: string | null;
      motivo_nao_enviada?: string | null;
      motivo_nao_enviada_outro?: string | null;
      fornecedor_atual?: string | null;
      data_fim_contrato?: string | null;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/envio-proposta`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async updateProspeccaoAssinadoDados(
    leadId: string,
    payload: {
      assinado?: boolean | null;
      data_assinatura?: string | null;
      motivo_nao_assinado?: string | null;
      motivo_nao_assinado_outro?: string | null;
      fornecedor_atual?: string | null;
      data_fim_contrato?: string | null;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/assinado-dados`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; lead?: ProspeccaoLead; message?: string };
  },

  async getProspeccaoLeadHistory(leadId: string) {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/history`,
      { headers: authHeaders() }
    );
    return data as { success: boolean; events: CccHistoryEvent[]; message?: string };
  },

  async prepareSolicitarPropostaProspeccaoLead(leadId: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/prepare-solicitar-proposta`,
      {},
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      lead?: ProspeccaoLead;
      marcacao_id?: string;
      message?: string;
    };
  },

  async getProspeccaoLeadMarcacao(leadId: string) {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/marcacao`,
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      marcacao?: Record<string, unknown>;
      marcacao_id?: string;
      message?: string;
    };
  },

  async getProspeccaoSignatureAssociateMarcacoes() {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/prospeccao/signature-associate-marcacoes`,
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      marcacoes?: Record<string, unknown>[];
      message?: string;
    };
  },

  async uploadProspeccaoProposalFile(leadId: string, formData: FormData) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/proposal/files`,
      formData,
      { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
    );
    return data as {
      success: boolean;
      file?: {
        id: string;
        lead_file_id: number;
        name: string;
        size: number;
        type: string;
        data?: string;
        field?: string;
        uploaded_at?: string;
      };
      error?: string;
      message?: string;
    };
  },

  async bulkReassignProspeccaoLeads(leadIds: string[], userId: number) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/bulk-reassign`,
      { lead_ids: leadIds, user_id: userId },
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      updated?: string[];
      skipped?: Array<{ lead_id: string; reason: string }>;
      updated_count?: number;
      message?: string;
    };
  },

  async deleteProspeccaoLead(leadId: string) {
    const { data } = await axios.delete(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}`,
      { headers: authHeaders() }
    );
    return data as { success: boolean; message?: string };
  },

  async getRecuperacaoMarcacoes(params?: {
    search?: string;
    assignee_status?: string;
    pool?: 'available' | 'mine' | 'workspace' | 'claimed';
    limit?: number;
    offset?: number;
    summary?: boolean;
    creation_date_from?: string;
  }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/recuperacao/marcacoes`, {
      headers: authHeaders(),
      params: {
        ...params,
        summary: params?.summary ? '1' : undefined,
      },
    });
    return data as {
      success: boolean;
      marcacoes: RecuperacaoMarcacao[];
      stats?: {
        total?: number;
        available?: number;
        mine?: number;
        with_deleted_or_suspended?: number;
      };
      pagination?: {
        total: number;
        limit: number;
        offset: number;
        has_more: boolean;
      };
    };
  },

  async claimRecuperacaoMarcacao(marcacaoId: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/claim`,
      {},
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async releaseRecuperacaoMarcacao(marcacaoId: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/release`,
      {},
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao };
  },

  async reverterRecuperacaoMarcacao(
    marcacaoId: string,
    payload?: { comercial_user_id?: number }
  ) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/reverter`,
      payload ?? {},
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async addRecuperacaoNote(marcacaoId: string, text: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/notes`,
      { text },
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async prepareSolicitarProposta(marcacaoId: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/prepare-solicitar-proposta`,
      {},
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async submitRecuperacaoProposal(
    marcacaoId: string,
    payload: { proposalData: Record<string, unknown> } & Record<string, unknown>
  ) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/proposal`,
      payload,
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      marcacao?: RecuperacaoMarcacao;
      marcacao_id?: string;
      proposta_id?: string;
      message?: string;
    };
  },

  async uploadRecuperacaoProposalFile(marcacaoId: string, formData: FormData) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/proposal/files`,
      formData,
      { headers: authHeadersMultipart() }
    );
    return data as { success: boolean; file?: Record<string, unknown>; error?: string; message?: string };
  },

  async uploadRecuperacaoContractFile(marcacaoId: string, formData: FormData) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/contract/files`,
      formData,
      { headers: authHeadersMultipart() }
    );
    return data as { success: boolean; file?: Record<string, unknown>; error?: string; message?: string };
  },

  async submitRecuperacaoContract(marcacaoId: string, contractData: Record<string, unknown>) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/contract`,
      { contractData },
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      marcacao?: RecuperacaoMarcacao;
      marcacao_id?: string;
      message?: string;
    };
  },

  async uploadProspeccaoContractFile(leadId: string, formData: FormData) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/contract/files`,
      formData,
      { headers: authHeadersMultipart() }
    );
    return data as { success: boolean; file?: Record<string, unknown>; error?: string; message?: string };
  },

  async submitProspeccaoContract(leadId: string, contractData: Record<string, unknown>) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/prospeccao/leads/${encodeURIComponent(leadId)}/contract`,
      { contractData },
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      lead?: ProspeccaoLead;
      marcacao_id?: string;
      message?: string;
    };
  },

  async searchRecuperacaoInvestigateMarcacoes(params: {
    nome_empresa?: string;
    nif?: string;
    morada?: string;
    codigo_postal?: string;
    contacto_cliente?: string;
    nome_responsavel?: string;
    ccc_user_id?: string;
    claim_status?: 'all' | 'available' | 'claimed';
    ccc_substage?: string;
    produtos?: string[];
    event_start_date?: string;
    event_end_date?: string;
    created_start_date?: string;
    created_end_date?: string;
    limit?: number;
  }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/recuperacao/investigate/marcacoes`, {
      headers: authHeaders(),
      params: {
        ...params,
        produtos: params.produtos?.length ? params.produtos : undefined,
      },
      paramsSerializer: (p) => {
        const parts: string[] = [];
        Object.entries(p).forEach(([key, value]) => {
          if (value == null || value === '') return;
          if (key === 'produtos' && Array.isArray(value)) {
            value.forEach((produto) => {
              if (produto) parts.push(`produtos=${encodeURIComponent(String(produto))}`);
            });
            return;
          }
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
        });
        return parts.join('&');
      },
    });
    return data as {
      success: boolean;
      data: Array<{
        id: number;
        marcacao_id: string;
        created_at: string | null;
        event_date: string | null;
        nome_empresa?: string;
        nome_responsavel?: string;
        contacto_cliente?: string;
        nif?: string | null;
        produto?: string;
        comercial_assignees?: string[];
        ccc_claimed_by?: number | null;
        ccc_claimed_by_name?: string | null;
        ccc_substage?: string | null;
        ccc_substage_label?: string | null;
        comercial_gestao_label?: string | null;
        estado_anterior_cliente?: string | null;
        estado_proposta_label?: string | null;
        is_available?: boolean;
        days_in_recuperacao?: number;
      }>;
      message?: string;
    };
  },

  async getRecuperacaoMarcacao(marcacaoId: string) {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}`,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async getRecuperacaoMarcacaoHistory(marcacaoId: string) {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/history`,
      { headers: authHeaders() }
    );
    return data as { success: boolean; events: RecuperacaoHistoryEvent[]; message?: string };
  },

  async bulkReassignRecuperacaoMarcacoes(marcacaoIds: string[], userId: number) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/bulk-reassign`,
      { marcacao_ids: marcacaoIds, user_id: userId },
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      updated?: string[];
      skipped?: Array<{ marcacao_id: string; reason: string }>;
      updated_count?: number;
      message?: string;
    };
  },

  async updateSemInteresseMotivo(
    marcacaoId: string,
    payload: { motivo_keys?: string[]; motivo_key?: string | null; feedback?: string }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/sem-interesse-motivo`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async updatePendenteMotivo(
    marcacaoId: string,
    payload: { motivo_keys?: string[]; motivo_key?: string | null; feedback?: string }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/pendente-motivo`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async updateAssinadoDados(
    marcacaoId: string,
    payload: {
      assinado?: boolean | null;
      data_assinatura?: string | null;
      motivo_nao_assinado?: string | null;
      motivo_nao_assinado_outro?: string | null;
      fornecedor_atual?: string | null;
      data_fim_contrato?: string | null;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/assinado-dados`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async updateEnvioProposta(
    marcacaoId: string,
    payload: {
      proposta_enviada?: boolean | null;
      data_envio?: string | null;
      enviado_para?: string | null;
      motivo_nao_enviada?: string | null;
      motivo_nao_enviada_outro?: string | null;
      fornecedor_atual?: string | null;
      data_fim_contrato?: string | null;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/envio-proposta`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async updateTentativasContacto(
    marcacaoId: string,
    payload: {
      items: Array<{ id?: string; date: string; time: string }>;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/tentativas-contacto`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async updateFidelizadosDados(
    marcacaoId: string,
    payload: {
      fornecedor_atual?: string | null;
      data_inicio_contrato?: string | null;
      servicos?: FidelizadosServicoItem[];
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/fidelizados-dados`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async updateRecuperacaoSubstage(marcacaoId: string, substage: string) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/recuperacao/marcacoes/${encodeURIComponent(marcacaoId)}/substage`,
      { substage },
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RecuperacaoMarcacao; message?: string };
  },

  async getRetencaoClients() {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/retencao/clients`, {
      headers: authHeaders(),
    });
    return data as {
      success: boolean;
      can_see_pool?: boolean;
      pool?: RetencaoClient[];
      clients?: RetencaoClient[];
      message?: string;
    };
  },

  async createRetencaoClient(payload: {
    nome_empresa: string;
    nif: string;
    data_cancelamento: string;
    motivo_cancelamento?: string | null;
    multa?: string | null;
    objetivo?: string | null;
    tempo_resposta?: string | null;
    google_maps_link?: string | null;
    source?: 'create' | 'upload';
  }) {
    const { data } = await axios.post(`${getCccApiBase()}/api/ccc/retencao/clients`, payload, {
      headers: authHeaders(),
    });
    return data as { success: boolean; client?: RetencaoClient; message?: string };
  },

  async updateRetencaoClient(
    clientId: string,
    payload: Partial<{
      nome_empresa: string;
      nif: string;
      data_cancelamento: string;
      motivo_cancelamento: string | null;
      multa: string | null;
      objetivo: string | null;
      tempo_resposta: string | null;
      google_maps_link: string | null;
    }>
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/clients/${encodeURIComponent(clientId)}`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; client?: RetencaoClient; message?: string };
  },

  async updateRetencaoClientSubstage(clientId: string, ccc_substage: string) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/clients/${encodeURIComponent(clientId)}/substage`,
      { ccc_substage },
      { headers: authHeaders() }
    );
    return data as { success: boolean; client?: RetencaoClient; message?: string };
  },

  async prepareRetencaoClientSolicitarProposta(clientId: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/clients/${encodeURIComponent(clientId)}/prepare-solicitar-proposta`,
      {},
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      client?: RetencaoClient;
      marcacao_id?: string;
      message?: string;
    };
  },

  async uploadRetencaoClientContractFile(clientId: string, formData: FormData) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/clients/${encodeURIComponent(clientId)}/contract/files`,
      formData,
      { headers: authHeaders() }
    );
    return data as { success: boolean; file?: Record<string, unknown>; error?: string; message?: string };
  },

  async submitRetencaoClientContract(clientId: string, contractData: Record<string, unknown>) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/clients/${encodeURIComponent(clientId)}/contract`,
      { contractData },
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao_id?: string; message?: string };
  },

  async getRetencaoClientMarcacao(clientId: string) {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/retencao/clients/${encodeURIComponent(clientId)}/marcacao`,
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      marcacao?: Record<string, unknown>;
      marcacao_id?: string;
      message?: string;
    };
  },

  async bulkAssignRetencaoClients(clientIds: string[], userId: number) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/clients/bulk-assign`,
      { client_ids: clientIds, user_id: userId },
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      updated?: string[];
      skipped?: Array<{ client_id: string; reason: string }>;
      updated_count?: number;
      message?: string;
    };
  },

  async bulkReassignRetencaoClients(clientIds: string[], userId: number) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/clients/bulk-reassign`,
      { client_ids: clientIds, user_id: userId },
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      updated?: string[];
      updated_count?: number;
      message?: string;
    };
  },

  async getRetencaoMarcacoes(params?: {
    search?: string;
    assignee_status?: string;
    pool?: 'available' | 'mine';
  }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/retencao/marcacoes`, {
      headers: authHeaders(),
      params,
    });
    return data as {
      success: boolean;
      marcacoes: RetencaoMarcacao[];
      stats?: {
        total?: number;
        available?: number;
        mine?: number;
        with_deleted_or_suspended?: number;
      };
    };
  },

  async claimRetencaoMarcacao(marcacaoId: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/claim`,
      {},
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async releaseRetencaoMarcacao(marcacaoId: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/release`,
      {},
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao };
  },

  async addRetencaoNote(marcacaoId: string, text: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/notes`,
      { text },
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async prepareRetencaoSolicitarProposta(marcacaoId: string) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/prepare-solicitar-proposta`,
      {},
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async submitRetencaoProposal(
    marcacaoId: string,
    payload: { proposalData: Record<string, unknown> } & Record<string, unknown>
  ) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/proposal`,
      payload,
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      marcacao?: RetencaoMarcacao;
      proposta_id?: string;
      message?: string;
    };
  },

  async uploadRetencaoProposalFile(marcacaoId: string, formData: FormData) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/proposal/files`,
      formData,
      { headers: authHeaders() }
    );
    return data as { success: boolean; file?: Record<string, unknown>; error?: string; message?: string };
  },

  async uploadRetencaoContractFile(marcacaoId: string, formData: FormData) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/contract/files`,
      formData,
      { headers: authHeaders() }
    );
    return data as { success: boolean; file?: Record<string, unknown>; error?: string; message?: string };
  },

  async submitRetencaoContract(marcacaoId: string, contractData: Record<string, unknown>) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/contract`,
      { contractData },
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async getRetencaoMarcacao(marcacaoId: string) {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}`,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async getRetencaoMarcacaoHistory(marcacaoId: string) {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/history`,
      { headers: authHeaders() }
    );
    return data as { success: boolean; events: RetencaoHistoryEvent[]; message?: string };
  },

  async bulkReassignRetencaoMarcacoes(marcacaoIds: string[], userId: number) {
    const { data } = await axios.post(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/bulk-reassign`,
      { marcacao_ids: marcacaoIds, user_id: userId },
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      updated?: string[];
      skipped?: Array<{ marcacao_id: string; reason: string }>;
      updated_count?: number;
      message?: string;
    };
  },

  async updateRetencaoSemInteresseMotivo(
    marcacaoId: string,
    payload: { motivo_keys?: string[]; motivo_key?: string | null; feedback?: string }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/sem-interesse-motivo`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async updateRetencaoPendenteMotivo(
    marcacaoId: string,
    payload: { motivo_keys?: string[]; motivo_key?: string | null; feedback?: string }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/pendente-motivo`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async updateRetencaoAssinadoDados(
    marcacaoId: string,
    payload: {
      assinado?: boolean | null;
      data_assinatura?: string | null;
      motivo_nao_assinado?: string | null;
      motivo_nao_assinado_outro?: string | null;
      fornecedor_atual?: string | null;
      data_fim_contrato?: string | null;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/assinado-dados`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async updateRetencaoEnvioProposta(
    marcacaoId: string,
    payload: {
      proposta_enviada?: boolean | null;
      data_envio?: string | null;
      enviado_para?: string | null;
      motivo_nao_enviada?: string | null;
      motivo_nao_enviada_outro?: string | null;
      fornecedor_atual?: string | null;
      data_fim_contrato?: string | null;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/envio-proposta`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async updateRetencaoTentativasContacto(
    marcacaoId: string,
    payload: {
      items: Array<{ id?: string; date: string; time: string }>;
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/tentativas-contacto`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async updateRetencaoFidelizadosDados(
    marcacaoId: string,
    payload: {
      fornecedor_atual?: string | null;
      data_inicio_contrato?: string | null;
      servicos?: FidelizadosServicoItem[];
    }
  ) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/fidelizados-dados`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async updateRetencaoSubstage(marcacaoId: string, substage: string) {
    const { data } = await axios.patch(
      `${getCccApiBase()}/api/ccc/retencao/marcacoes/${encodeURIComponent(marcacaoId)}/substage`,
      { substage },
      { headers: authHeaders() }
    );
    return data as { success: boolean; marcacao?: RetencaoMarcacao; message?: string };
  },

  async getDashboardStats() {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/recuperacao/dashboard/stats`, {
      headers: authHeaders(),
    });
    return data;
  },

  async getCccDashboardClientsWorked(params: {
    start_date?: string;
    end_date?: string;
    workspaces?: string;
    users?: string;
    statuses?: string;
  }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/dashboard/clients-worked`, {
      headers: authHeaders(),
      params,
    });
    return data as {
      success: boolean;
      start_date: string;
      end_date: string;
      count: number;
      clients: CccClientWorked[];
      message?: string;
    };
  },

  async getCccDashboardStatusOptions() {
    const { data } = await axios.get(
      `${getCccApiBase()}/api/ccc/dashboard/clients-worked/status-options`,
      { headers: authHeaders() }
    );
    return data as {
      success: boolean;
      statuses: Array<{ value: string; label: string }>;
      message?: string;
    };
  },

  async getCccDashboardClientNotes(params: {
    workspace: CccWorkspaceKind;
    entity_id: string;
  }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/dashboard/clients-worked/notes`, {
      headers: authHeaders(),
      params,
    });
    return data as {
      success: boolean;
      workspace: CccWorkspaceKind;
      entity_id: string;
      notes: CccLeadNote[];
      message?: string;
    };
  },

  async getCccDashboardProposalRequests(params: {
    from_date?: string;
    to_date?: string;
    start_date?: string;
    end_date?: string;
    workspaces?: string;
    users?: string;
  }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/dashboard/proposal-requests`, {
      headers: authHeaders(),
      params,
    });
    return data as {
      success: boolean;
      from_date: string;
      to_date: string;
      total: number;
      rows: CccProposalStatRow[];
      by_status: Array<{ label: string; count: number }>;
      by_servico: Array<{ key: string; label: string; count: number }>;
      message?: string;
    };
  },

  async getCccDashboardContractSubmissions(params: {
    from_date?: string;
    to_date?: string;
    start_date?: string;
    end_date?: string;
    workspaces?: string;
    users?: string;
  }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/dashboard/contract-submissions`, {
      headers: authHeaders(),
      params,
    });
    return data as {
      success: boolean;
      from_date: string;
      to_date: string;
      total: number;
      rows: CccContractSubmissionRow[];
      by_status: Array<{ status: string; label: string; count: number }>;
      message?: string;
    };
  },

  async getCccDashboardSignatureRequests(params: {
    from_date?: string;
    to_date?: string;
    start_date?: string;
    end_date?: string;
    workspaces?: string;
    users?: string;
  }) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/dashboard/signature-requests`, {
      headers: authHeaders(),
      params,
    });
    return data as {
      success: boolean;
      from_date: string;
      to_date: string;
      total: number;
      rows: CccSignatureStatRow[];
      by_status: Array<{ status: string; label: string; count: number }>;
      message?: string;
    };
  },

  async getCccDashboardProposalTargetUsers() {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/dashboard/proposal-target-users`, {
      headers: authHeaders(),
    });
    return data as {
      success: boolean;
      users: Array<{
        id: number;
        name: string;
        email?: string | null;
        status: 'active' | 'suspended' | 'deleted';
      }>;
      message?: string;
    };
  },

  async getCccDashboardUsers() {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/dashboard/users`, {
      headers: authHeaders(),
    });
    return data as {
      success: boolean;
      users: Array<{ id: number; name: string; email?: string | null; tenant?: string | null }>;
      message?: string;
    };
  },

  async searchWorkspace(workspace: CccWorkspaceKind, q: string) {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/workspace/search`, {
      headers: authHeaders(),
      params: { workspace, q: q.trim() },
    });
    return data as {
      success: boolean;
      matches?: CccWorkspaceSearchMatch[];
      query?: string;
      message?: string;
    };
  },

  async getCccWorkspaceDuplicateFlags() {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/workspace/duplicate-flags`, {
      headers: authHeaders(),
    });
    return data as {
      success: boolean;
      prospeccao?: Record<string, { duplicate_phone?: boolean; duplicate_nif?: boolean }>;
      recuperacao?: Record<string, { duplicate_phone?: boolean; duplicate_nif?: boolean }>;
      message?: string;
    };
  },

  async validateCccAccess() {
    const url = `${getCccApiBase()}/api/ccc/access/validate`;
    const { data, status } = await axios.get(url, {
      headers: authHeaders(),
      params: { target: 'ccc' },
      validateStatus: (s) => s === 200 || s === 403,
    });
    if (status === 403 && data && data.allowed !== true) {
      return { ...data, allowed: false };
    }
    return data;
  },

  async health() {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/health`);
    return data;
  },
};

export default cccApi;
